import type { EnrichedStockData, StockData } from '@/types/chart';
import { OhlcvIndex } from '@/types/chart';
import { elderRay, ema, last, rsi, stochasticOscillator } from 'react-financial-charts';
import type { ActivePopup, ChartId, ChartInfo, MoreProps, RenderNodesMap } from '../types/commontypes';
import { keys } from './utils';
import type { ResizeBarConfig } from '../components/ChartElements/ChartResizer';
import { globalOptions } from '../config/globalOptions';

// Import candle data JSON files
import candlesBTC15m from '../assets/candles_BTC_15m.json';
import candlesBTC1h from '../assets/candles_BTC_1h.json';
import candlesETH15m from '../assets/candles_ETH_15m.json';
import candlesETH1h from '../assets/candles_ETH_1h.json';
import candlesDOGE15m from '../assets/candles_DOGE_15m.json';
import candlesDOGE1h from '../assets/candles_DOGE_1h.json';

/** Price formatter - use globalOptions.priceFormatter first */
export const pricesDisplayFormat = (value: number) => globalOptions.priceFormatter(value);

export const strokeStyleCallback = (d: StockData) => {
  const close = d.ohlcv[OhlcvIndex.CLOSE];
  const open = d.ohlcv[OhlcvIndex.OPEN];
  if (close > open) return '#26a69a';
  if (close < open) return '#ef5350';
  return '#000000';
};

export const volumeColor = (data: StockData) => {
  return data.ohlcv[OhlcvIndex.CLOSE] > data.ohlcv[OhlcvIndex.OPEN]
    ? 'rgba(38, 166, 154, 0.3)'
    : 'rgba(239, 83, 80, 0.3)';
};

export const volumeSeries = (data: StockData) => data.ohlcv[OhlcvIndex.VOLUME];

export const openCloseColor = (data: StockData) => {
  return data.ohlcv[OhlcvIndex.CLOSE] > data.ohlcv[OhlcvIndex.OPEN] ? '#26a69a' : '#ef5350';
};

export const barChartExtents = (data: StockData) => data.ohlcv[OhlcvIndex.VOLUME];

export const candleChartExtents = (data: StockData) => [data.ohlcv[OhlcvIndex.HIGH], data.ohlcv[OhlcvIndex.LOW]];

// O H L C accesor
export const ohlcAccessor = (candle: StockData) => {
  return {
    open: Number(candle.ohlcv[OhlcvIndex.OPEN]),
    high: Number(candle.ohlcv[OhlcvIndex.HIGH]),
    low: Number(candle.ohlcv[OhlcvIndex.LOW]),
    close: Number(candle.ohlcv[OhlcvIndex.CLOSE]),
  };
};

export const yEdgeIndicator = (data: StockData) => {
  if (!data.ohlcv) {
    console.log('yEdgeIndicator', data);
  }
  return data.ohlcv[OhlcvIndex.CLOSE];
};

export type IndicatorWithAccessor = ((data: unknown) => unknown[]) & {
  accessor: () => (d: unknown) => unknown;
  stroke: () => string;
  options: () => Record<string, unknown>;
};

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const ema12 = ema()
  .id(1)
  .options({ windowSize: 12 })
  .merge((d: Record<string, unknown>, c: unknown) => {
    d['ema12'] = c;
  })
  .accessor((d: Record<string, unknown>) => d?.['ema12']) as unknown as IndicatorWithAccessor;

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const ema26 = ema()
  .id(2)
  .options({ windowSize: 26 })
  .merge((d: Record<string, unknown>, c: unknown) => {
    d['ema26'] = c;
  })
  .accessor((d: Record<string, unknown>) => d?.['ema26']) as unknown as IndicatorWithAccessor;

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const elder = elderRay()
  .merge((d: Record<string, unknown>, c: unknown) => {
    d['elder'] = c;
  })
  .accessor((d: Record<string, unknown>) => d?.['elder']) as unknown as IndicatorWithAccessor;

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const rsiCalculator = rsi()
  .options({ windowSize: 14 })
  .merge((d: { rsi: unknown }, c: unknown) => {
    d.rsi = c;
  })
  .accessor((d: { rsi: unknown }) => d?.rsi) as unknown as IndicatorWithAccessor;

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const fullSTO = stochasticOscillator()
  .options({ windowSize: 14, kWindowSize: 3, dWindowSize: 4 })
  .merge((d: { fullSTO: unknown }, c: unknown) => {
    d.fullSTO = c;
  })
  .accessor((d: { fullSTO: unknown }) => d?.fullSTO) as unknown as IndicatorWithAccessor;

// Symbol and timeframe types
export type SymbolType = 'BTC' | 'ETH' | 'DOGE';
export type TimeframeType = '15m' | '1h';

/** JSON 파일의 기존 캔들 형식 */
interface LegacyCandleData {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

/** 기존 형식의 캔들 데이터를 새 ohlcv 형식으로 변환 */
const convertToStockData = (d: LegacyCandleData): StockData => {
  const { date, open, high, low, close, volume, ...rest } = d;
  return {
    ohlcv: [
      new Date(date).getTime(),
      open,
      high,
      low,
      close,
      volume,
      0, // quoteVolume (not available in legacy data)
    ] as [number, number, number, number, number, number, number],
    ...rest,
  };
};

// Raw candle data map - convert from legacy JSON format
const rawCandleData: Record<SymbolType, Record<TimeframeType, LegacyCandleData[]>> = {
  BTC: {
    '15m': candlesBTC15m,
    '1h': candlesBTC1h,
  },
  ETH: {
    '15m': candlesETH15m,
    '1h': candlesETH1h,
  },
  DOGE: {
    '15m': candlesDOGE15m,
    '1h': candlesDOGE1h,
  },
};

// Process candles with indicators
const processCandles = (candles: LegacyCandleData[]): EnrichedStockData[] => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return fullSTO(rsiCalculator(elder(ema26(ema12(candles)))))
    .filter((d: unknown) => d != null)
    .map((d: unknown) => ({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      ...convertToStockData(d as LegacyCandleData),
      elder: elder.accessor()(d),
      rsi: rsiCalculator.accessor()(d),
      fullSTO: fullSTO.accessor()(d),
    })) as EnrichedStockData[];
};

// Pre-calculated data for all symbols and timeframes
export const calculatedDataBySymbol: Record<SymbolType, Record<TimeframeType, EnrichedStockData[]>> = {
  BTC: {
    '15m': processCandles(rawCandleData.BTC['15m']),
    '1h': processCandles(rawCandleData.BTC['1h']),
  },
  ETH: {
    '15m': processCandles(rawCandleData.ETH['15m']),
    '1h': processCandles(rawCandleData.ETH['1h']),
  },
  DOGE: {
    '15m': processCandles(rawCandleData.DOGE['15m']),
    '1h': processCandles(rawCandleData.DOGE['1h']),
  },
};

// Legacy support - default to BTC
const dataTimeframes = ['15m', '1h'] as const;
export const calculatedData = dataTimeframes.reduce(
  (acc, timeframe) => {
    acc[timeframe] = calculatedDataBySymbol.BTC[timeframe];
    return acc;
  },
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
  {} as Record<(typeof dataTimeframes)[number], EnrichedStockData[]>,
);

/**
 * Generates resize bars between charts
 * @param chartInfos chart heights
 * @param margin margin
 */
export const getResizeBars = (chartInfos: ChartInfo[], margin: { top: number; bottom: number }): ResizeBarConfig[] => {
  const bars: ResizeBarConfig[] = [];
  let accumulatedFromTop = margin.top;

  // iterate from bottom → up
  for (let i = 1; i < chartInfos.length; i++) {
    const top = chartInfos[i - 1];
    const bottom = chartInfos[i];

    accumulatedFromTop += top.height;

    bars.push({
      topId: top.chartId,
      bottomId: bottom.chartId,
      top: accumulatedFromTop,
    });
  }
  // reverse so bars are in natural top → bottom order
  return bars;
};

export const getNextChartOrder = (renderNodes: RenderNodesMap, chartId: ChartId) => {
  let maxOrder = 0;

  const chartShapes = renderNodes[chartId];
  if (chartShapes) {
    for (const type of keys(chartShapes)) {
      const shapes = chartShapes[type] || [];
      shapes.forEach((shape: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const s = shape as Record<string, unknown>;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        if (s['order'] && (s['order'] as number) > maxOrder) {
          maxOrder = s['order'] as number; // eslint-disable-line @typescript-eslint/no-unsafe-type-assertion
        }
      });
    }
  }
  return maxOrder + 1;
};

// Outside your component or inside (before JSX), define this helper function
export const handleSquareClick = (
  value: number,
  e: React.MouseEvent,
  moreProps: MoreProps,
  chartContainerRef: React.RefObject<HTMLDivElement | null>,
  setYCoordinateValue: (val: number) => void,
  setYAccessorValue: (value: number | undefined) => void,
  setActivePopup: (popup: ActivePopup) => void,
  chartId: ChartId,
  yAccessor: (d: unknown) => number | undefined,
) => {
  if (chartContainerRef.current) {
    const rect = chartContainerRef.current.getBoundingClientRect();
    const coords = {
      x: e.clientX - rect.left - 230,
      y: e.clientY - rect.top + 10,
    };
    const lastItem = last(moreProps.plotData ?? [], yAccessor);
    setYCoordinateValue(value);
    setYAccessorValue(yAccessor(lastItem));
    setActivePopup({ type: 'y-popup', coords, chartId });
  }
};
